
import React, { useCallback, useEffect, useRef } from 'react';
import { AppState, Agent, AgentType, NodeType } from '../types';
import { AppAction } from '../store/reducer';
import { createAgent, processSimulationTick } from '../services/simulationLogic';
import { SIMULATION_TICK_RATE } from '../constants';

export const useSimulation = (
  appState: AppState,
  dispatch: React.Dispatch<AppAction>
) => {
  const simTickRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const accumulatorRef = useRef(0);
  const requestRef = useRef<number | null>(null);
  const stateRef = useRef(appState);

  useEffect(() => {
    stateRef.current = appState;
  }, [appState]);

  const updateSimulation = useCallback((time: number) => {
    const s = stateRef.current;
    if (!s.isRunning) {
      requestRef.current = null;
      return;
    }

    if (lastUpdateRef.current === 0) lastUpdateRef.current = time;
    const deltaTime = Math.min(250, time - lastUpdateRef.current);
    lastUpdateRef.current = time;
    accumulatorRef.current += deltaTime * s.simulationSpeed;
    
    try {
      let nodes = s.nodes;
      let agents = s.agents;
      let stats = s.stats;
      let processedSteps = 0;

      while (accumulatorRef.current >= SIMULATION_TICK_RATE && processedSteps < 10) {
        simTickRef.current += 1;
        const result = processSimulationTick(
          nodes,
          agents,
          stats,
          s.isRunning,
          s.isAutoSpawning,
          s.autoSpawnRate,
          1,
          s.simConfig,
          s.patientPatience,
          simTickRef.current,
          s.layoutVersion,
          s.pixelsPerMeter
        );
        nodes = result.nodes;
        agents = result.agents;
        stats = result.stats;
        accumulatorRef.current -= SIMULATION_TICK_RATE;
        processedSteps += 1;
      }

      if (processedSteps > 0) dispatch({
          type: 'SIM_TICK', 
          payload: { agents, nodes, stats }
      });
    } catch (error) {
      console.error("Simulation tick crashed:", error);
      dispatch({ type: 'TOGGLE_RUN' });
    }

    requestRef.current = requestAnimationFrame(updateSimulation);
  }, [dispatch]);

  useEffect(() => {
    if (appState.isRunning) {
      if (requestRef.current === null) {
        requestRef.current = requestAnimationFrame(updateSimulation);
      }
    } else {
      lastUpdateRef.current = 0;
      accumulatorRef.current = 0;
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
    }
    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [appState.isRunning, updateSimulation]);

  useEffect(() => {
    if (!appState.isRunning && appState.agents.length === 0 && appState.stats.treatedPatients === 0) {
      simTickRef.current = 0;
    }
  }, [appState.isRunning, appState.agents.length, appState.stats.treatedPatients]);

  const spawnAgent = useCallback((type: AgentType) => {
    const s = stateRef.current;
    const startNode = (type === AgentType.PATIENT_TRAUMA || type === AgentType.PATIENT_STROKE) 
      ? s.nodes.find(n => n.type === NodeType.AMBULANCE_ENTRANCE) || s.nodes[0]
      : s.nodes.find(n => n.type === NodeType.ENTRANCE) || s.nodes[0];
    
    if (!startNode) return;
    const agent = createAgent(type, startNode, simTickRef.current, s.patientPatience, s.simConfig);
    dispatch({ type: 'SPAWN_AGENTS', payload: [agent] });
  }, [dispatch]);

  const triggerMCI = useCallback((mciType: 'EPIDEMIC' | 'ACCIDENT' | 'BIOHAZARD') => {
    const s = stateRef.current;
    const entrance = s.nodes.find(n => n.type === NodeType.ENTRANCE) || s.nodes[0];
    const ambulance = s.nodes.find(n => n.type === NodeType.AMBULANCE_ENTRANCE) || s.nodes[0];
    
    if (!entrance || !ambulance) return;

    const newAgents: Agent[] = [];
    const count = 10;
    for (let i = 0; i < count; i++) {
      const type = mciType === 'EPIDEMIC' ? AgentType.PATIENT_INFECTIOUS : AgentType.PATIENT_TRAUMA;
      const node = mciType === 'EPIDEMIC' ? entrance : ambulance;
      newAgents.push(createAgent(type, node, simTickRef.current, s.patientPatience, s.simConfig));
    }
    
    dispatch({ type: 'SPAWN_AGENTS', payload: newAgents });
    dispatch({ type: 'UPDATE_STATS', payload: { criticalEvents: s.stats.criticalEvents + (mciType === 'ACCIDENT' ? count : 0) } });
  }, [dispatch]);

  return { spawnAgent, triggerMCI };
};
